<!-- src/views/SecurityOfficerLoginsView.vue -->
<template>
  <div class="container">
    <div class="sticky-header-form">
      <h3 class="text-center">SECURITY OFFICER LOGINS</h3>

      <!-- Create / Edit form -->
      <div class="d-flex align-items-center justify-content-center flex-wrap gap-3 mb-4">
        <div class="d-flex align-items-center gap-2">
          <label class="fw-medium">Officer</label>
          <Multiselect
            v-model="selectedOfficerId"
            :options="officers.map(o => ({ value: o.id, label: o.name }))"
            placeholder="Select or type officer..."
            :searchable="true"
            :clearable="true"
            style="min-width: 220px; --ms-border-color: #dee2e6; --ms-radius: 0.375rem; --ms-px: 0.75rem; --ms-py: 0.25rem;"
          />
        </div>
        <div class="d-flex align-items-center gap-2">
          <label class="fw-medium">Username</label>
          <input type="text" v-model="userName" placeholder="Login username" class="border rounded px-3 py-1">
        </div>
        <div class="d-flex align-items-center gap-2">
          <label class="fw-medium">Password</label>
          <input type="password" v-model="password" :placeholder="editingId ? 'Leave blank to keep unchanged' : 'Login password'" class="border rounded px-3 py-1">
        </div>
        <button @click="submitForm" class="bg-primary text-white px-6 py-1 rounded hover:bg-blue-700" :disabled="isBusy">
          {{ submitButtonLabel }}
        </button>
        <button v-if="editingId" @click="cancelEdit" class="bg-secondary text-white px-6 py-1 rounded hover:bg-gray-600" :disabled="isBusy">
          Cancel
        </button>
      </div>

      <div class="d-flex align-items-center justify-content-center gap-2 mb-2">
        <label class="fw-medium">Search</label>
        <input type="text" v-model="searchQuery" placeholder="Search by username or officer name..." class="border rounded px-3 py-1" style="min-width: 260px;">
      </div>
    </div>

    <!-- Table -->
    <div class="list-scroll-container overflow-x-auto">
      <table class="border-collapse border mx-auto">
        <thead>
          <tr class="bg-warning">
            <th class="border p-2 text-left">Username</th>
            <th class="border p-2 text-left">Officer</th>
            <th class="border p-2 text-left">Created At</th>
            <th class="border p-2 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!filteredLogins || filteredLogins.length === 0">
            <td colspan="4" class="border p-2 text-center text-gray-500">{{ searchQuery ? 'No matching records' : 'No records found' }}</td>
          </tr>
          <tr v-for="(login, index) in filteredLogins" :key="index">
            <td class="border p-2 text-left">{{ login.userName || '-' }}</td>
            <td class="border p-2 text-left">{{ login.securityOfficerName || '-' }}</td>
            <td class="border p-2 text-left">{{ login.createdAt ? new Date(login.createdAt).toLocaleString() : '-' }}</td>
            <td class="border p-2 text-left">
              <button @click="startEdit(login)" class="text-blue-600 hover:underline mr-3" :disabled="isBusy">Edit</button>
              <button @click="deleteLogin(login)" class="text-red-600 hover:underline" :disabled="isBusy">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import Multiselect from '@vueform/multiselect'
import '@vueform/multiselect/themes/default.css'

const props = defineProps({
  baseUrl: { type: String, required: true }
})

const officers = ref([])
const selectedOfficerId = ref(null)
const userName = ref('')
const password = ref('')
const isLoading = ref(false)
const isCreating = ref(false)
const isBusy = ref(false)
const logins = ref([])
const editingId = ref(null)
const searchQuery = ref('')

const submitButtonLabel = computed(() => {
  if (editingId.value) return isCreating.value ? 'Updating...' : 'Update Login'
  return isCreating.value ? 'Creating...' : 'Create Login'
})

const filteredLogins = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) return logins.value
  return logins.value.filter(l =>
    (l.userName || '').toLowerCase().includes(query) ||
    (l.securityOfficerName || '').toLowerCase().includes(query)
  )
})

const loadOfficers = () => {
  const cachedData = localStorage.getItem("lookupData")
  if (cachedData) {
    officers.value = JSON.parse(cachedData).officers || []
  }
}

const loadLogins = async () => {
  if (!localStorage.token) { return }

  isLoading.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "Login/GetSecurityOfficerLogins", {
      method: "GET",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" }
    })
    if (!response.ok) throw new Error(await response.text())
    const data = await response.json()
    logins.value = data.logins || []
  } catch (e) {
    alert('Failed to load security officer logins: ' + e.message)
  } finally {
    isLoading.value = false
    isBusy.value = false
  }
}

const resetForm = () => {
  editingId.value = null
  userName.value = ''
  password.value = ''
  selectedOfficerId.value = null
}

const startEdit = (login) => {
  editingId.value = login.id
  userName.value = login.userName
  password.value = ''
  selectedOfficerId.value = login.securityOfficerId
}

const cancelEdit = () => {
  resetForm()
}

const submitForm = () => {
  if (editingId.value) {
    updateLogin()
  } else {
    createLogin()
  }
}

const createLogin = async () => {
  if (!selectedOfficerId.value) { alert('Please select an officer'); return }
  if (!userName.value || !password.value) { alert('Please enter both username and password'); return }
  if (!localStorage.token) { alert('Please login first'); return }

  isCreating.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "Login/CreateSecurityOfficerLogin", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify({ userName: userName.value, password: password.value, securityOfficerId: selectedOfficerId.value })
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || data.Error || 'Login could not be created.')

    logins.value.push(data.login)
    resetForm()
    alert('Security officer login created successfully.')
  } catch (e) {
    alert('Failed to create security officer login: ' + e.message)
  } finally {
    isCreating.value = false
    isBusy.value = false
  }
}

const updateLogin = async () => {
  if (!selectedOfficerId.value) { alert('Please select an officer'); return }
  if (!userName.value) { alert('Please enter a username'); return }
  if (!localStorage.token) { alert('Please login first'); return }

  isCreating.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "Login/UpdateSecurityOfficerLogin", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify({ id: editingId.value, userName: userName.value, password: password.value, securityOfficerId: selectedOfficerId.value })
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || data.Error || 'Login could not be updated.')

    const index = logins.value.findIndex(l => l.id === editingId.value)
    if (index !== -1) { logins.value[index] = data.login }
    resetForm()
    alert('Security officer login updated successfully.')
  } catch (e) {
    alert('Failed to update security officer login: ' + e.message)
  } finally {
    isCreating.value = false
    isBusy.value = false
  }
}

const deleteLogin = async (login) => {
  if (!localStorage.token) { alert('Please login first'); return }
  if (!confirm(`Delete login "${login.userName}"? This cannot be undone.`)) { return }

  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "Login/DeleteSecurityOfficerLogin", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify({ id: login.id })
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || data.Error || 'Login could not be deleted.')

    logins.value = logins.value.filter(l => l.id !== login.id)
    if (editingId.value === login.id) { resetForm() }
  } catch (e) {
    alert('Failed to delete security officer login: ' + e.message)
  } finally {
    isBusy.value = false
  }
}

onMounted(() => {
  loadOfficers()
  loadLogins()
})
</script>

<style scoped>
.sticky-header-form {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  padding-top: 1rem;
  padding-bottom: 0.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.list-scroll-container {
  max-height: 60vh;
  overflow-y: auto;
}
</style>
